export interface JsonToken {text: string; kind: string;}
export interface LogMessagePart {text: string; isJson: boolean; isUrl?: boolean; tokens?: JsonToken[];}

export function formatLogMessage(message: string): LogMessagePart[] {
    const parts: LogMessagePart[] = [];
    let textStart = 0;
    for (let start = 0; start < message.length; start++) {
        if (message[start] !== '{' && message[start] !== '[') continue;
        const end = findJsonEnd(message, start);
        if (end < 0) continue;
        const candidate = message.slice(start, end);
        try {JSON.parse(candidate);} catch {continue;}
        if (start > textStart) parts.push(...formatPlainText(message.slice(textStart, start)));
        const formatted = indentJson(candidate);
        parts.push({text: formatted, isJson: true, tokens: tokenizeJson(formatted)});
        textStart = end;
        start = end - 1;
    }
    if (textStart < message.length) parts.push(...formatPlainText(message.slice(textStart)));
    return parts;
}

function formatPlainText(text: string): LogMessagePart[] {
    const parts: LogMessagePart[] = [];
    const urls = /https?:\/\/[^\s<>"']+/gi;
    let start = 0;
    for (const match of text.matchAll(urls)) {
        const index = match.index!;
        // Keep sentence punctuation outside the link; balanced URL parentheses remain intact.
        let url = match[0].replace(/[.,;:!?]+$/, '');
        while (url.endsWith(')') && (url.match(/\)/g)?.length || 0) > (url.match(/\(/g)?.length || 0)) url = url.slice(0, -1);
        if (index > start) parts.push({text: text.slice(start, index), isJson: false});
        parts.push({text: url, isJson: false, isUrl: true});
        start = index + url.length;
    }
    if (start < text.length) parts.push({text: text.slice(start), isJson: false});
    return parts;
}

function tokenizeJson(json: string): JsonToken[] {
    const tokens: JsonToken[] = [];
    const pattern = /"(?:\\[\s\S]|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null/g;
    let start = 0;
    for (const match of json.matchAll(pattern)) {
        const index = match.index!;
        if (index > start) tokens.push({text: json.slice(start, index), kind: 'plain'});
        const text = match[0];
        const kind = text.startsWith('"')
            ? (json.slice(index + text.length).trimStart().startsWith(':') ? 'key' : 'string')
            : text === 'true' || text === 'false' ? 'boolean' : text === 'null' ? 'null' : 'number';
        tokens.push({text, kind});
        start = index + text.length;
    }
    if (start < json.length) tokens.push({text: json.slice(start), kind: 'plain'});
    return tokens;
}

function findJsonEnd(message: string, start: number): number {
    const stack: string[] = [];
    let quoted = false;
    let escaped = false;
    for (let index = start; index < message.length; index++) {
        const character = message[index];
        if (quoted) {
            if (escaped) escaped = false;
            else if (character === '\\') escaped = true;
            else if (character === '"') quoted = false;
            continue;
        }
        if (character === '"') quoted = true;
        else if (character === '{' || character === '[') stack.push(character);
        else if (character === '}' || character === ']') {
            if (stack.pop() !== (character === '}' ? '{' : '[')) return -1;
            if (!stack.length) return index + 1;
        }
    }
    return -1;
}

function indentJson(json: string): string {
    // Format tokens directly so large numeric IDs and escaped strings stay unchanged.
    const tokens = json.match(/"(?:\\[\s\S]|[^"\\])*"|[^\s]/g) || [];
    let result = '';
    let depth = 0;
    const newline = () => '\n' + '  '.repeat(depth);
    for (let index = 0; index < tokens.length; index++) {
        const token = tokens[index];
        if (token === '{' || token === '[') {
            result += token;
            depth++;
            if (tokens[index + 1] !== '}' && tokens[index + 1] !== ']') result += newline();
        } else if (token === '}' || token === ']') {
            depth--;
            if (tokens[index - 1] !== '{' && tokens[index - 1] !== '[') result += newline();
            result += token;
        } else if (token === ',') result += ',' + newline();
        else if (token === ':') result += ': ';
        else result += token;
    }
    return result;
}
